import React from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

export type RichTextEditorVariant =
  | "default"
  | "modal"
  | "outer"
  | "minimal"
  | "extended"
  | "sample"
  | "plain";

const BUTTONS = {
  default: [
    ["undo", "redo", "font", "fontSize", "formatBlock"],
    ["bold", "underline", "italic", "removeFormat"],
    [
      "fontColor",
      "hiliteColor",
      "outdent",
      "indent",
      "align",
      "horizontalRule",
      "list",
    ],
    ["fullScreen", "link"],
  ],
  modal: [
    ["undo", "redo", "font", "fontSize", "formatBlock"],
    ["bold", "underline", "italic", "removeFormat"],
    [
      "fontColor",
      "hiliteColor",
      "outdent",
      "indent",
      "align",
      "horizontalRule",
      "list",
      "lineHeight",
    ],
    ["fullScreen", "link", "save"],
  ],
  outer: [
    ["undo", "redo", "font", "fontSize", "formatBlock"],
    ["bold", "underline", "italic", "removeFormat"],
    [
      "fontColor",
      "hiliteColor",
      "outdent",
      "indent",
      "align",
      "horizontalRule",
      "list",
    ],
    ["fullScreen", "link", "save"],
  ],
  minimal: [
    ["undo", "redo"],
    ["bold", "underline", "italic", "removeFormat"],
    ["fontColor", "hiliteColor"],
    ["align", "list", "link"],
    ["fullScreen", "codeView"],
  ],
  extended: [
    ["undo", "redo", "font", "fontSize", "formatBlock"],
    [
      "bold",
      "underline",
      "italic",
      "strike",
      "subscript",
      "superscript",
      "removeFormat",
    ],
    [
      "fontColor",
      "hiliteColor",
      "outdent",
      "indent",
      "align",
      "horizontalRule",
      "list",
      "table",
    ],
    ["fullScreen", "showBlocks", "codeView", "link", "image", "save"],
  ],
  sample: [
    ["undo", "redo", "font", "fontSize", "formatBlock"],
    [
      "bold",
      "underline",
      "italic",
      "strike",
      "subscript",
      "superscript",
      "removeFormat",
    ],
    [
      "fontColor",
      "hiliteColor",
      "outdent",
      "indent",
      "align",
      "horizontalRule",
      "list",
      "table",
    ],
    [
      "link",
      "image",
      "video",
      "fullScreen",
      "showBlocks",
      "codeView",
      "preview",
      "print",
      "save",
    ],
  ],
} as const;

export default function RichTextEditor({
  value,
  onChange,
  height,
  editorKey,
  variant = "default",
  readOnly,
}: {
  value?: string;
  onChange: (html: string) => void;
  height?: string | number;
  editorKey?: string;
  variant?: RichTextEditorVariant;
  readOnly?: boolean;
}) {
  const options: Record<string, unknown> = {};
  if (variant !== "plain") {
    options.buttonList = BUTTONS[variant as keyof typeof BUTTONS];
  }

  return (
    <SunEditor
      key={editorKey}
      setContents={value || ""}
      onChange={onChange}
      height={height}
      readOnly={readOnly}
      {...(Object.keys(options).length ? { setOptions: options as any } : {})}
    />
  );
}
